// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { HotOutcomeIconSvg, MymatchMLetter } from '../../assets/svg';
import { PlusSymbolSvg, ValidCheckmarkSvg } from '../../assets/svg/common';
import OutcomeLoaderSvgAsComponent from '../../assets/svg/common/asComponents/OutcomeLoaderSvgAsComponent';
import { Target } from '../../client';
import NumberFormatter from '../../i18n/numberformatter';
import darkTheme from '../../styled-components/dark';
import { HotTag } from '../../styled-components/shared';
import { type OddButtonThemesType } from '../../types/buttons';
import { type MatchViewMode, MatchViewModes } from '../../types/matchView';
import { type OutcomeMode } from '../../types/outcome';
import { OutcomeModes } from '../../types/outcome';
import { SportIds, type SportId } from '../../types/sports';
import OddUpdateIndicator from '../OddUpdateIndicator';

const HOT_BOUNCING_ANIMATION_DURATION = 5000; // ms
const HOT_BOUNCING_ANIMATION_DELAY = Math.round(HOT_BOUNCING_ANIMATION_DURATION / 5); // ms
const HOT_ANIMATION_BORDER_WIDTH = 1; // em
const HOT_TRANSPARENT_BORDER_PADDING = 3; // px
const HOT_CONIC_GRADIENT_BORDER_SPACING = 2 * HOT_TRANSPARENT_BORDER_PADDING; // px

const useShouldShowUpdateIndicator = (matchViewMode) => {
  const [showOddUpdateIndicator, setShowUpdateIndicator] = useState(true);
  const showUpdateIndicatorTimeoutRef = useRef(null);

  useEffect(() => {
    setShowUpdateIndicator(false);

    showUpdateIndicatorTimeoutRef.current = setTimeout(() => {
      setShowUpdateIndicator(true);
    }, 0);
  }, [matchViewMode]);

  useEffect(() => {
    return () => {
      if (showUpdateIndicatorTimeoutRef.current) {
        clearTimeout(showUpdateIndicatorTimeoutRef.current);
      }
    };
  }, []);

  return showOddUpdateIndicator;
};

const hotButtonBreathAnimation = keyframes`
  0% {
    transform: scale(1);
  }

  10% {
    transform: scale(1.03);
  }

  20% {
    transform: scale(1);
  }

  25% {
    transform: scale(1);
  }

  35% {
    transform: scale(1.03);

  }

  45% {
    transform: scale(1);
  }

  100% {
    transform: scale(1);
  }
`;

const ContentBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  transition: none 100ms ease-in-out;
  transition-property: background-color, transform;
  pointer-events: none;
  transform-style: preserve-3d;

  ${({ $btnTheme, $selected, $isHot, $mode }) => css`
    ${$btnTheme
      ? css`
          background-color: ${$btnTheme.backgroundColor};
          border: ${$btnTheme.borderColor ? `solid 1px ${$btnTheme.borderColor}` : 'none'};
        `
      : undefined}

    ${!$selected && $mode === OutcomeModes.PLAY && $isHot
      ? css`
          animation: ${hotButtonBreathAnimation} ${HOT_BOUNCING_ANIMATION_DURATION}ms linear infinite;
        `
      : undefined}
  `};
`;

const Wrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-height: 3em;
  border-radius: 0.5em;
  padding: 0.3em;
  font-size: 0.9em;
  -webkit-tap-highlight-color: transparent;
  pointer-events: var(--OddButton-pointerEvents, auto);

  user-select: none;
  cursor: ${({ $mode }) => ($mode === OutcomeModes.PLAY ? 'pointer' : 'auto')};
  line-height: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  &:active {
    ${ContentBox} {
      transform: scale(0.98);
    }
  }

  ${({ $selected, $disableClick, $btnTheme }) => css`
    ${
      $selected || $disableClick
        ? css`
            pointer-events: auto;
          `
        : undefined
    }

    ${
      $btnTheme
        ? css`
            ${Target.__MOBILE__
              ? undefined
              : css`
                  &:hover {
                    ${ContentBox} {
                      background-color: ${({ $btnTheme }) => `${$btnTheme.backgroundColor}e0`};
                    }
                  }
                `}
          `
        : undefined
    }}
  `}
`;

const conicGradientAngleCSSVarName = '--conic-gradient-angle';

const ConicGradientBorder = styled.div`
  position: absolute;
  top: -${HOT_CONIC_GRADIENT_BORDER_SPACING}px;
  left: -${HOT_CONIC_GRADIENT_BORDER_SPACING}px;
  bottom: -${HOT_CONIC_GRADIENT_BORDER_SPACING}px;
  right: -${HOT_CONIC_GRADIENT_BORDER_SPACING}px;

  border-radius: 0.8em;
  z-index: 1;

  padding: ${Math.round(HOT_TRANSPARENT_BORDER_PADDING / 2)}px;

  @supports (
    ((-webkit-mask-composite: xor) or (-webkit-mask-composite: subtract) or (mask-composite: substract)) and
      ((-webkit-mask: linear-gradient(#fff 0 0)) or (mask: linear-gradient(#fff 0 0)))
  ) {
    background-image: conic-gradient(
      from var(${conicGradientAngleCSSVarName}, 0deg),
      ${darkTheme.HotEffect.mainEffectColor},
      #ffffff33,
      #ffffff33,
      #ffffff33,
      #ffffff33,
      ${darkTheme.HotEffect.mainEffectColor}
    );
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: subtract;
    -webkit-mask-composite: subtract;
    -webkit-mask-composite: xor;
    border: none;
  }
`;

const DarkBorder = styled.div`
  position: absolute;
  top: -${HOT_TRANSPARENT_BORDER_PADDING}px;
  left: -${HOT_TRANSPARENT_BORDER_PADDING}px;
  width: calc(100% + ${2 * HOT_TRANSPARENT_BORDER_PADDING}px);
  height: calc(100% + ${2 * HOT_TRANSPARENT_BORDER_PADDING}px);
  border-radius: 0.8em;
  background-color: transparent;
  z-index: 1;
`;

const HotNbUsersCounter = styled(HotTag)`
  position: absolute;
  top: -0.8em;
  right: 0.5em;
  z-index: 1;
`;

const HotNbUsersCounterLabel = styled.div`
  margin-right: 0.2em;
`;

const HotNbUsersCounterIcon = styled(HotOutcomeIconSvg)`
  width: 0.9em;
  height: auto;
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Title = styled.div`
  position: relative;
  font-size: 0.9em;
  color: ${({ $btnTheme }) => $btnTheme.titleColor};
  font-weight: 400;
  letter-spacing: 1px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 1px 0;
  width: 100%;
`;

const ValueWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const sharedValueStyles = css`
  transition: opacity 50ms ease-in-out;

  ${({ $freezeOpacity }) => css`
    ${$freezeOpacity
      ? css`
          opacity: 1;
        `
      : css`
          opacity: var(--OddButton-valueOpacity, 1);
        `}
  `}
`;

const Value = styled.span`
  position: relative;
  text-align: center;
  color: ${({ $btnTheme }) => $btnTheme.valueColor};
  font-weight: bold;
  font-size: 1.4em;

  ${sharedValueStyles}

  ${({ $btnTheme }) => {
    if ($btnTheme && $btnTheme.strikeThrough) {
      return css`
        &:after {
          position: absolute;
          content: '';
          left: -20%;
          top: 50%;
          width: 140%;
          height: 0.05em;
          background-color: ${$btnTheme.valueColor}};
        }
      `;
    }
  }}
`;

const StyledOddUpdateIndicator = styled(OddUpdateIndicator)`
  position: absolute;
  width: 0.8em;
  right: 0.5em;
  bottom: 0;
  height: 100%;
  display: var(--OddButton-oddUpdateIndicatorDisplay, flex);
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  position: absolute;
  pointer-events: none;
  background: transparent;
  left: 50%;
  top: 50%;
`;

const myMatchLogoWrapperTransitionIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(2);
  }

  25% {
    opacity: 1;
    transform: scale(0.3);
  }

  50% {
    opacity: 1;
    transform: scale(1.3);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const myMatchLogoWrapperTransitionOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const MyMatchLogoWrapper = styled.div`
  position: absolute;
  left: 0.3em;
  bottom: 0.2em;

  width: 1.1em;
  height: 1.1em;
`;

const MyMatchLogoAnimationWrapper = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 100%;

  ${({ $btnTheme }) => css`
    background-color: ${$btnTheme.mmLogoBgColor};
    color: ${$btnTheme.mmLogoColor};
  `}

  ${({ $shouldBeAnimated }) =>
    $shouldBeAnimated
      ? css`
          &.mymatch-logo-wrapper-transitions-enter,
          &.mymatch-logo-wrapper-transitions-enter-done {
            animation: ${myMatchLogoWrapperTransitionIn} 500ms ease-in-out forwards;
          }

          &.mymatch-logo-wrapper-transitions-exit,
          &.mymatch-logo-wrapper-transitions-exit-done {
            animation: ${myMatchLogoWrapperTransitionOut} 200ms ease-in-out forwards;
          }
        `
      : css`
          &.mymatch-logo-wrapper-transitions-enter,
          &.mymatch-logo-wrapper-transitions-enter-done {
            animation: ${myMatchLogoWrapperTransitionOut} 500ms ease-in-out reverse forwards;
          }

          &.mymatch-logo-wrapper-transitions-exit,
          &.mymatch-logo-wrapper-transitions-exit-done {
            animation: ${myMatchLogoWrapperTransitionOut} 200ms ease-in-out forwards;
          }
        `}
`;

const StyledMymatchMLetter = styled(MymatchMLetter)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  height: 45%;
  width: 45%;
`;

const WARNING_ICON_PADDING = 0.8; // em

const WarningIcon = styled.div`
  position: relative;
  width: 2em;
  height: 2em;

  font-size: 80%;

  padding: ${WARNING_ICON_PADDING / 3}em ${WARNING_ICON_PADDING}em;
  border-radius: 50%;

  ${({ $hasTitle }) =>
    $hasTitle
      ? css`
          margin-top: 0.1em;
        `
      : undefined}

  ${({ $btnTheme }) =>
    $btnTheme
      ? css`
          background-color: #300303;
        `
      : null}
`;

const WarningSymbol = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-weight: bold;
  font-size: 150%;

  ${({ $btnTheme }) =>
    $btnTheme
      ? css`
          color: ${$btnTheme.backgroundColor};
        `
      : null}
`;

const validCheckmarkKeyframes = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const ValidCheckmark = styled(ValidCheckmarkSvg)`
  height: 1.4em;
  width: 1.4em;

  & path {
    stroke: white;

    animation: ${validCheckmarkKeyframes} 300ms ease-in-out forwards;
  }

  ${sharedValueStyles}
`;

const PlusSymbol = styled(PlusSymbolSvg)`
  height: 1.4em;
  width: 1.4em;

  & path {
    stroke: #aa0909;
  }

  ${sharedValueStyles}
`;

const outcomeLoaderKeyframes = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const OutcomeLoader = styled(OutcomeLoaderSvgAsComponent)`
  position: absolute;
  height: 1.4em;
  width: 1.4em;
  animation: ${outcomeLoaderKeyframes} 800ms ease-in-out infinite;

  transition: opacity 50ms ease-in-out;

  ${({ $selected, $freezeOpacity }) => css`
    ${$selected
      ? css`
          --OutcomeLoaderSvgAsComponentPrimaryColor: white;
          --OutcomeLoaderSvgAsComponentSecondaryColor: #aa0909;
        `
      : css`
          --OutcomeLoaderSvgAsComponentPrimaryColor: #aa0909;
          --OutcomeLoaderSvgAsComponentSecondaryColor: #aa0909;
        `}

    ${$freezeOpacity
      ? css`
          opacity: 0;
        `
      : css`
          opacity: var(--OddButton-loaderOpacity, 0);
        `}
  `}
`;

const hotBounceAnimationsSharedStyle = css`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 0.9em;
  pointer-events: none;
  box-shadow: ${darkTheme.HotEffect.mainEffectColor} 0px 0px 1em 1em inset;
  transform: translate3d(0, 0, -1px);
`;

const bounceKeyframes = keyframes`
  0% {
    top: 0em;
    left: 0em;
    width: 100%;
    height: 100%;

    opacity: 1;
  }

  18% {
    opacity: 0;
  }

  20% {
    top: -${HOT_ANIMATION_BORDER_WIDTH}em;
    left: -${HOT_ANIMATION_BORDER_WIDTH}em;
    width: calc(100% + ${HOT_ANIMATION_BORDER_WIDTH * 2}em);
    height: calc(100% + ${HOT_ANIMATION_BORDER_WIDTH * 2}em);
  }

  100% {
    opacity: 0;
  }
`;

const HotFirstBounceAnimation = styled.div`
  ${hotBounceAnimationsSharedStyle}

  animation: ${bounceKeyframes} ${HOT_BOUNCING_ANIMATION_DURATION}ms ease-in-out infinite;
`;

const HotSecondBounceAnimation = styled.div`
  ${hotBounceAnimationsSharedStyle}

  animation: ${bounceKeyframes} ${HOT_BOUNCING_ANIMATION_DURATION}ms ease-in-out infinite ${HOT_BOUNCING_ANIMATION_DELAY}ms;
`;

type PropsType = {
  title: string;
  value: number;
  mode?: OutcomeMode;
  selected?: boolean;
  isForbidden?: boolean;
  onClick?: () => void;
  oddId: number;
  uuid?: string;
  disableClick?: boolean;
  theme?: OddButtonThemesType;
  matchViewMode?: MatchViewMode;
  futureBetIsSuspended?: boolean;
  sportId: SportId;
  displayMMOddsValue?: boolean;
  hotNbUsers?: number;
  className?: string;
};

const OddButton = ({
  title,
  value,
  mode = OutcomeModes.PLAY,
  isForbidden = false,
  selected = false,
  onClick = () => {},
  oddId,
  uuid = '',
  disableClick = false,
  theme = null,
  matchViewMode = MatchViewModes.CLASSIC,
  futureBetIsSuspended = false,
  displayMMOddsValue,
  sportId,
  hotNbUsers,
  className = '',
}: PropsType) => {
  const currRAFPid = useRef();
  const conicGradientBorderRef = useRef();

  const isHot = !!hotNbUsers;
  const showOddUpdateIndicator = useShouldShowUpdateIndicator(matchViewMode);
  const freezeOpacity = useMemo(() => mode !== OutcomeModes.PLAY || selected, [mode, selected]);
  const btnTheme = useMemo(() => {
    if (theme) {
      return darkTheme.OddButton[theme];
    }

    if (mode) {
      if (mode === OutcomeModes.PAUSE) {
        if (matchViewMode === MatchViewModes.CLASSIC) {
          return {
            ...darkTheme.OddButton.disabled,
            strikeThrough: !isForbidden,
          };
        }

        if (matchViewMode === MatchViewModes.MY_MATCH) {
          return {
            ...darkTheme.OddButton.mmAnyError,
            strikeThrough: !isForbidden,
          };
        }
      }

      if (mode === OutcomeModes.STOP || mode === OutcomeModes.STOP_BUT_DISPLAYED) {
        if (matchViewMode === MatchViewModes.MY_MATCH) {
          return {
            ...darkTheme.OddButton.mmAnyError,
            strikeThrough: !isForbidden,
          };
        }

        return darkTheme.OddButton.disabled;
      }

      if (mode === OutcomeModes.MY_MATCH_ANY_ERROR) {
        if (selected) {
          return darkTheme.OddButton.active;
        }

        return {
          ...darkTheme.OddButton.mmAnyError,
          strikeThrough: sportId === SportIds.FOOTBALL && !!value && futureBetIsSuspended,
        };
      }

      if (selected) {
        return darkTheme.OddButton.active;
      }
    }

    return darkTheme.OddButton.normal;
  }, [theme, mode, selected, matchViewMode, isForbidden, sportId, value, futureBetIsSuspended]);

  const displayedValue = useMemo(() => {
    if (selected && mode === OutcomeModes.MY_MATCH_ANY_ERROR) {
      return;
    }

    if (value && value > 0 && mode !== OutcomeModes.STOP && mode !== OutcomeModes.MY_MATCH_ANY_ERROR) {
      return NumberFormatter.formatOdd(value);
    }

    return '-';
  }, [mode, selected, value]);

  const onButtonClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  const content = useMemo(() => {
    if (matchViewMode === MatchViewModes.MY_MATCH && mode === OutcomeModes.MY_MATCH_ANY_ERROR && selected) {
      return (
        <WarningIcon $btnTheme={btnTheme} $hasTitle={title !== ''}>
          <WarningSymbol $btnTheme={btnTheme}>!</WarningSymbol>
        </WarningIcon>
      );
    }

    if (matchViewMode === MatchViewModes.MY_MATCH && selected) {
      return <ValidCheckmark $freezeOpacity={freezeOpacity} />;
    }

    if (
      matchViewMode === MatchViewModes.MY_MATCH &&
      !selected &&
      !displayMMOddsValue &&
      displayedValue &&
      displayedValue !== '-'
    ) {
      return <PlusSymbol $freezeOpacity={freezeOpacity} />;
    }

    if (displayedValue) {
      return (
        <Value $btnTheme={btnTheme} $freezeOpacity={freezeOpacity}>
          {displayedValue}
        </Value>
      );
    }
  }, [matchViewMode, mode, selected, displayMMOddsValue, displayedValue, btnTheme, title, freezeOpacity]);

  const [blockOddUpdateIndicatorRendering, setBlockOddUpdateIndicatorRendering] = useState(false);

  useLayoutEffect(() => {
    const styleObserver = new MutationObserver((mutations) => {
      const { target: mutationTarget } = mutations[0] ?? {};

      if (mutationTarget) {
        // $FlowFixMe
        const currentValue = mutations[0].target.style.getPropertyValue('--OddButton-showOddUpdateIndicator');

        if (currentValue === '0') {
          setBlockOddUpdateIndicatorRendering(true);
        }

        if (currentValue === '1') {
          setBlockOddUpdateIndicatorRendering(false);
        }
      }
    });

    const documentElement = document.documentElement;

    if (documentElement) {
      styleObserver.observe(documentElement, {
        attributes: true,
        attributeFilter: ['style'],
      });

      return () => {
        styleObserver.disconnect();
      };
    }
  }, []);

  const mmLogoShouldBeAnimated = useMemo(
    () =>
      !~[
        OutcomeModes.MY_MATCH_ANY_ERROR,
        OutcomeModes.PAUSE,
        OutcomeModes.STOP,
        OutcomeModes.STOP_BUT_DISPLAYED,
      ].indexOf(mode),
    [mode],
  );

  useLayoutEffect(() => {
    const conicGradientBorderNode = conicGradientBorderRef.current;
    const timeout = 4000;

    if (conicGradientBorderNode && isHot) {
      const startTime = performance.now();

      const RAFCallback = () => {
        const elapsedTime = performance.now() - startTime;
        const progressRatio = elapsedTime / timeout;
        const angle = (progressRatio * 360) % 360;

        conicGradientBorderNode.style.setProperty(conicGradientAngleCSSVarName, `${angle}deg`);

        requestAnimationFrame(RAFCallback);
      };

      currRAFPid.current = requestAnimationFrame(RAFCallback);

      return () => {
        if (currRAFPid.current) {
          cancelAnimationFrame(currRAFPid.current);
        }
      };
    }
  }, [isHot]);

  return (
    <Wrapper
      className={`${className} odd-button-wrapper`}
      $mode={mode}
      $btnTheme={btnTheme}
      onClick={onButtonClick}
      $disableClick={disableClick}
      $selected={selected}
      data-testid={`odd-button-${oddId}`}
      $isHot={isHot}
    >
      {hotNbUsers ? (
        <HotNbUsersCounter data-testid={`hot-outcome-${oddId}-counter`}>
          <HotNbUsersCounterLabel>{hotNbUsers}</HotNbUsersCounterLabel>
          <HotNbUsersCounterIcon />
        </HotNbUsersCounter>
      ) : null}
      <ContentBox $btnTheme={btnTheme} $selected={selected} $isHot={isHot} $mode={mode}>
        {isHot && mode === OutcomeModes.PLAY ? (
          <>
            {!selected ? (
              <>
                <HotFirstBounceAnimation />
                <HotSecondBounceAnimation />
              </>
            ) : null}
            <ConicGradientBorder ref={conicGradientBorderRef} />
            <DarkBorder />
          </>
        ) : null}
      </ContentBox>
      <Content>
        <Center id={oddId ? `odd-button-${oddId}-${uuid}` : null} />
        {title !== '' ? <Title $btnTheme={btnTheme}>{title}</Title> : ''}
        <ValueWrapper>
          {content}
          <OutcomeLoader $selected={selected} $freezeOpacity={freezeOpacity} />
        </ValueWrapper>
        {mode === OutcomeModes.PLAY &&
        showOddUpdateIndicator &&
        (matchViewMode !== MatchViewModes.MY_MATCH || !selected) &&
        !blockOddUpdateIndicatorRendering ? (
          <StyledOddUpdateIndicator value={value} isWhite={selected} />
        ) : null}
      </Content>
      <MyMatchLogoWrapper className="my-match-logo-wrapper">
        <CSSTransition
          in={matchViewMode === MatchViewModes.MY_MATCH}
          timeout={mmLogoShouldBeAnimated ? 500 : 200}
          classNames="mymatch-logo-wrapper-transitions"
          unmountOnExit
          mountOnEnter
        >
          <MyMatchLogoAnimationWrapper $shouldBeAnimated={mmLogoShouldBeAnimated} $btnTheme={btnTheme}>
            <StyledMymatchMLetter />
          </MyMatchLogoAnimationWrapper>
        </CSSTransition>
      </MyMatchLogoWrapper>
    </Wrapper>
  );
};

export default React.memo<unknown>(OddButton);
